import React, { useState, useEffect } from 'react';

import { motion, AnimatePresence } from 'motion/react';
import { MapPin, ArrowRight } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import { RestaurantListModal } from './RestaurantListModal';
import { ExperiencesSection } from './ExperiencesSection';
import './BestOfArea.css';

const CATEGORIES = [
  { id: 'pizza', name: 'Best Pizza', emoji: '🍕', dishType: 'Pizza' },
  { id: 'burger', name: 'Best Burger', emoji: '🍔', dishType: 'Burger' },
  { id: 'sushi', name: 'Best Sushi', emoji: '🍣', dishType: 'Sushi' },
  { id: 'taco', name: 'Best Tacos', emoji: '🌮', dishType: 'Taco' },
  { id: 'ramen', name: 'Best Ramen', emoji: '🍜', dishType: 'Ramen' },
  { id: 'pasta', name: 'Best Pasta', emoji: '🍝', dishType: 'Pasta' },
  { id: 'curry', name: 'Best Curry', emoji: '🍛', dishType: 'Curry' },
  { id: 'steak', name: 'Best Steak', emoji: '🥩', dishType: 'Steak' },
  { id: 'seafood', name: 'Best Seafood', emoji: '🦞', dishType: 'Seafood' },
  { id: 'dessert', name: 'Best Dessert', emoji: '🍰', dishType: 'Dessert' },
  { id: 'chinese', name: 'Best Chinese', emoji: '🥡', cuisine: 'Chinese' },
  { id: 'italian', name: 'Best Italian', emoji: '🇮🇹', cuisine: 'Italian' },
  { id: 'mexican', name: 'Best Mexican', emoji: '🇲🇽', cuisine: 'Mexican' },
  { id: 'japanese', name: 'Best Japanese', emoji: '🇯🇵', cuisine: 'Japanese' },
  { id: 'indian', name: 'Best Indian', emoji: '🇮🇳', cuisine: 'Indian' },
  { id: 'thai', name: 'Best Thai', emoji: '🇹🇭', cuisine: 'Thai' }
];

function BestOfArea({ userLocation, onLocationChange }) {

  const [currentLocation, setCurrentLocation] = useState(userLocation || '');
  const [locationInput, setLocationInput] = useState('');
  const [showLocationInput, setShowLocationInput] = useState(!userLocation);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (userLocation) {
      setCurrentLocation(userLocation);
    } else {
      // Try to get user's location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            // In production, you'd reverse geocode this to get city name
            setCurrentLocation('Your Location');
            if (onLocationChange) {
              onLocationChange('Your Location');
            }
          },
          () => {
            // Location access denied or failed
            setShowLocationInput(true);
          }
        );
      } else {
        setShowLocationInput(true);
      }
    }
  }, [userLocation, onLocationChange]);

  const handleLocationSubmit = (e) => {
    e.preventDefault();
    if (locationInput.trim()) {
      setCurrentLocation(locationInput.trim());
      if (onLocationChange) {
        onLocationChange(locationInput.trim());
      }
      setShowLocationInput(false);
      setLocationInput('');
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category.name);
  };

  // Map categories to include images for dish categories
  const categoryWithImages = CATEGORIES.map(cat => {
    const imageMap = {
      'pizza': 'https://images.unsplash.com/photo-1651978595428-ce1c3b3cc493?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvcnxlbnwxfHx8fDE3NjQwOTA5MjJ8MA&ixlib=rb-4.1.0&q=80&w=1080',
      'burger': 'https://images.unsplash.com/photo-1664988851353-10e4c865b35f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXJnZXIlMjBjbG9zZSUyMHVwJTIwZm9vZHxlbnwxfHx8fDE3NjQwOTcyNjF8MA&ixlib=rb-4.1.0&q=80&w=1080',
      'sushi': 'https://images.unsplash.com/photo-1570780775848-bc1897788ce0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXNoaSUyMG5pZ2lyaSUyMHBsYXR0ZXJ8ZW58MXx8fHwxNzY0MDk3MjYxfDA&ixlib=rb-4.1.0&q=80&w=1080',
      'taco': 'https://images.unsplash.com/photo-1698854632942-35b25aab3fa6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0YWNvcyUyMG1leGljYW4lMjBzdHJlZXR8ZW58MXx8fHwxNzY0MDk3MjYyfDA&ixlib=rb-4.1.0&q=80&w=1080',
      'ramen': 'https://images.unsplash.com/photo-1653697469316-955d5ebe5994?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyYW1lbiUyMG5vb2RsZXMlMjBib3dsfGVufDF8fHx8MTc2NDA0MjUwOHww&ixlib=rb-4.1.0&q=80&w=1080',
      'pasta': 'https://images.unsplash.com/photo-1749169337822-d875fd6f4c9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwYXN0YSUyMGl0YWxpYW4lMjBkaXNofGVufDF8fHx8MTc2Mzk4NzU1OHww&ixlib=rb-4.1.0&q=80&w=1080',
    };
    return {
      ...cat,
      image: imageMap[cat.id] || null,
      subtitle: cat.dishType ? `${cat.dishType} dishes` : cat.cuisine ? `${cat.cuisine} cuisine` : ''
    };
  });

  // Separate dish categories and cuisine categories
  const dishCategories = categoryWithImages.filter(cat => cat.dishType);
  const cuisineCategories = categoryWithImages.filter(cat => cat.cuisine);

  return (
    <div id="best-of-area" className="container mx-auto px-8">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-20 pt-8"
      >
        <div className="w-1 h-20 bg-border mx-auto mb-8"></div>
        <h1 className="serif text-7xl mb-6">Local Favorites</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-12">
          Discover the finest dishes in {currentLocation || 'your neighborhood'}
        </p>

        {/* Location Input */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-xl mx-auto"
        >
          {showLocationInput ? (
            <form onSubmit={handleLocationSubmit} className="flex items-center gap-4">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  placeholder="Enter your city or location"
                  className="w-full pl-10 pr-4 h-12 border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <button
                type="submit"
                className="px-8 h-12 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-all duration-300 text-xs uppercase tracking-widest whitespace-nowrap"
              >
                Set Location
              </button>
            </form>
          ) : (
            <button
              onClick={() => setShowLocationInput(true)}
              className="px-6 py-2 border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors text-sm uppercase tracking-widest"
            >
              Change Location
            </button>
          )}
        </motion.div>
      </motion.div>

      {/* Dish Categories */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mb-24"
      >
        <div className="mb-12">
          <h2 className="serif text-4xl mb-3">Popular Dishes</h2>
          <p className="text-sm text-muted-foreground">Explore top-rated dishes in your area</p>
        </div>

        <div className="grid grid-cols-3 gap-8">
          {dishCategories.map((category, index) => (
            <motion.article
              key={category.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className="group cursor-pointer"
              onClick={() => handleCategoryClick(category)}
            >
              {/* Image */}
              {category.image && (
                <div className="relative aspect-[4/3] overflow-hidden mb-5">
                  <ImageWithFallback
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              )}

              {/* Content */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="serif text-2xl mb-1 group-hover:text-muted-foreground transition-colors">
                    {category.name}
                  </h3>
                  {category.subtitle && (
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                      {category.subtitle}
                    </p>
                  )}
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.article>
          ))}
        </div>
      </motion.section>

      {/* Cuisine Types */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="border-t border-border pt-16"
      >
        <div className="mb-12">
          <h2 className="serif text-4xl mb-3">Cuisines</h2>
          <p className="text-sm text-muted-foreground">Browse by culinary tradition</p>
        </div>

        <div className="grid grid-cols-2 gap-x-16 gap-y-8">
          {cuisineCategories.map((cuisine, index) => (
            <motion.div
              key={cuisine.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="group cursor-pointer pb-6 border-b border-border hover:border-foreground transition-colors"
              onClick={() => handleCategoryClick(cuisine)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="serif text-3xl mb-2 group-hover:text-muted-foreground transition-colors">
                    {cuisine.name}
                  </h3>
                  {cuisine.subtitle && (
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                      {cuisine.subtitle}
                    </p>
                  )}
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Exclusive Dining Experiences */}
      <ExperiencesSection />

      {/* Restaurant List Modal */}
      <AnimatePresence>
        {selectedCategory && (
          <RestaurantListModal
            categoryName={selectedCategory}
            onClose={() => setSelectedCategory(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default BestOfArea;

